/**
 * Database API for firebase to data related operations - fetching, pushing, updating, etc.
 *
 * This is an interface between the official Firebase API and the AutoI webapp.
 */

import {dbase, databaseServices} from './firebase';
import {PRIVACY_PUBLIC} from "../components/recording/utils";
import {STATUS_LIVE_STREAM_START} from "../components/unit/liveview/utils";


/* -----------------    User Extension Table     ------------------ */
/**
 * The default user schema to be sent up into firebase
 * for a given UUID when a new user is created.
 * This is an extension of
 *
 * @type {{creationDate: Object, email: null, fcmToken: null, lastAccessed: Object, units: {}, verifiedEmail: boolean}}
 */
const DEFAULT_USER_SCHEMA = {
    creationDate: databaseServices.ServerValue.TIMESTAMP, // TODO: Unnecessary schema.
    email: null, // TODO: Unnecessary schema.
    fcmToken: null,
    lastAccessed: databaseServices.ServerValue.TIMESTAMP, // TODO: Unnecessary schema.
    units: {},
    verifiedEmail: false // TODO: Unnecessary schema.
};

/**
 * Creates a user on firebase database using the
 * extension schema {@see DEFAULT_USER_SCHEMA}
 * that extends the authentication table.
 *
 * @param uid - Firebase UUID. {@type IMMUTABLE}
 * @param email - signed up with application
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const doCreateUser = (uid, email) => {
    // Check if the data exists before adding it in
    dbase.ref(`users/${uid}`).once('value').then(snapshot => {
        if (!snapshot.exists()) {
            return dbase.ref(`users/${uid}`).set({
                ...DEFAULT_USER_SCHEMA,
                email: email // TODO: Unnecessary schema.
            });
        }
    });
};

/**
 * Gets all users from firebase at once.
 *
 * @returns {firebase.Promise<*>|*|Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getAllUsersOnce = () =>
    dbase.ref('users').once('value');

/**
 * Gets a particular user from firebase at a point in time, given a
 * @param uid - firebase unique user ID (UUID)
 *
 * @returns {firebase.Promise<*>|*|Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getUserOnce = (uid) =>
    dbase.ref(`users/${uid}`).once('value');

/**
 * Get units belonging (paired) with a user id once.
 * @param uid - firebase unique user ID (UUID)
 * @returns {firebase.Promise<*>|*|Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getUnitsByUserOnce = (uid) =>
    dbase.ref(`users/${uid}/units`).once('value');

/**
 * Pass a controller {@param _function} to manage changes to the
 * authorized user table that corresponds to the given {@param uid}
 * of the user in the firebase database.
 *
 * In other words, this is a function that allows us to listen to
 * changes to the data for the current user
 * (w.r.t. the database, NOT the authentication API).
 *
 * @param _function receives a snapshot that contains the data at the
 * specified location in the database at the time of the event.
 *
 * For more information and examples, visit
 * {@link https://firebase.google.com/docs/database/web/read-and-write}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const doOnUserValueChanged = (uid, _function) =>
    dbase.ref(`users/${uid}`).on('value', _function);

/**
 * Pass a controller {@param _function} to manage changes to the
 * authorized user's unit table that corresponds to the given
 * {@param uid} of the user in the firebase database.
 *
 * @param _function receives a snapshot that contains the data at the
 * specified location in the database at the time of the event.
 *
 * For more information and examples, visit
 * {@link https://firebase.google.com/docs/database/web/read-and-write}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const doOnUnitsByUserValueChanged = (uid, _function) =>
    dbase.ref(`users/${uid}/units`).on('value', _function);


/* -----------------    Unit Table    ------------------ */
/**
 * Get a unit given a unitId once.
 * @param unitId of the Unit
 * @returns {firebase.Promise<*>|*|Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getUnitByUnitIdOnce = (unitId) =>
    dbase.ref(`units/${unitId}`).once('value');

/**
 * Sets a listener {@param _function}
 * that receives a single parameter
 * snapshot to listen to change events
 * of a particular unit given a
 * {@param unitId} of the Unit
 * @returns {firebase.Promise<*>|*|Promise<any>}
 */
export const doOnUnitValueChanged = (unitId, _function) =>
    dbase.ref(`units/${unitId}`).on('value', _function);

/**
 * Removes all listeners attached
 * to a particular {@param unitId}
 */
export const doOffUnitValueChanged = (unitId) =>
    dbase.ref(`units/${unitId}`).off();

/**
 * Stars the live stream of the
 * {@param unitId} by updating it's status,
 * listened by the device.
 */
export const setLiveStreamStart = (unitId) =>
    dbase.ref(`units/${unitId}`).update({liveStreamStatus: STATUS_LIVE_STREAM_START});


/* -----------------    Recordings Table    ------------------ */
/**
 * Get a single recording given a unitID and a recordingID once.
 * @param unitId of the Unit
 * @param recordingId of the recording
 * @returns {Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getRecordingOnce = (unitId, recordingId) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`)
        .once('value');

/**
 * Sets a listener {@param _function} to a
 * single recording given a unitID and a recordingID.
 * @param unitId of the Unit
 * @param recordingId of the recording
 * @returns {Promise<any>}
 */
export const doOnRecordingValueChanged = (unitId, recordingId, _function) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`)
        .on('value', _function);

/**
 * Removes all listeners attached to a recording.
 * @param unitId of the Unit
 * @param recordingId of the recording
 * @returns {Promise<any>}
 */
export const doOffRecordingValueChanged = (unitId, recordingId) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`).off();

/**
 * Get ALL recordings given a unitID once.
 * @param unitId of the Unit
 * @returns {Promise<any>}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getRecordingsOnce = (unitId) =>
    dbase.ref(`recordings/${unitId}`)
        .orderByKey()   // Sort by insertion order (oldest to newest)
        .once('value');

/**
 * Get recordings given a
 * {@param unitId} of the Unit once, up to the latest
 * {@param limit}ed number of recordings.
 * @returns {Promise<any>}
 *
 * To understand why getRecordingsOnceUpToLimit was created for
 * infinite scrolling, read through this
 * {@link https://hackernoon.com/infinite-scrolling-in-firebase-e28bfbc53578}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getRecordingsOnceUpToLimit = (unitId, limit) =>
    dbase.ref(`recordings/${unitId}`)
        .orderByKey()   // Sort by insertion order (oldest to newest)
        .limitToLast(limit)
        .once('value');

/**
 * Get recordings given a
 * {@param unitId} of the Unit once, up to the latest
 * {@param limit}ed number of recordings, ending on
 * {@param endOn} key.
 * @returns {Promise<any>}
 *
 * To understand why getRecordingsOnceUpToLimitEndOn was created for
 * infinite scrolling, read through this
 * {@link https://hackernoon.com/infinite-scrolling-in-firebase-e28bfbc53578}
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const getRecordingsOnceUpToLimitEndOn = (unitId, limit, endOn) =>
    dbase.ref(`recordings/${unitId}`)
        .orderByKey()   // Sort by insertion order (oldest to newest)
        .endAt(endOn)
        .limitToLast(limit)
        .once('value');

/**
 * Sets a particular recording's unread status to false
 * (i.e. call this when the recording has been read)
 *
 * {@param unitId} which the recording belongs to.
 * {@param recordingId} of the recording.
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const setRecordingIsRead = (unitId, recordingId) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`).update({isUnread: false});

/**
 * Sets a particular recording's privacy to {@code PRIVACY_PUBLIC}
 * (i.e. call this when the recording is allowed to be shared)
 *
 * {@param unitId} which the recording belongs to.
 * {@param recordingId} of the recording.
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const setRecordingPublic = (unitId, recordingId) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`).update({privacy: PRIVACY_PUBLIC});

/**
 * Sets a particular recording's unread status to false
 * (i.e. call this when the recording has been read)
 *
 * {@param unitId} which the recording belongs to.
 * {@param recordingId} of the recording.
 * {@param newData} object that contains the key value pairs of the data to be updated
 *
 * NOTE: Asynchronous promise call from firebase has to be handled.
 */
export const setUpdatedRecordingData = (unitId, recordingId, newData) =>
    dbase.ref(`recordings/${unitId}/${recordingId}`).update(newData);



// WEBPACK FOOTER //
// ./src/firebase/dbase.js